/**
 * Organizations — the vendor and retail orgs behind the claims.
 *
 * Verification here is per-brand, not global: an org becomes verified against a
 * brand when an admin approves its claim, which is why the CTA points at the
 * approvals queue rather than offering a verify action on this table.
 *
 * Create/edit/delete/invite live here too — re-homed from the old (unreachable)
 * AdminOrganizationManagementPage, which was the only UI for them.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  brandDirectoryApi,
  OrganizationDetail,
  OrganizationDirectoryRow,
  OrganizationMutationPayload,
  OrganizationRole,
  OrganizationStatus,
  OrganizationType,
  OrganizationVendorType,
} from '../../services/brandDirectoryApi';
import {
  bvCardSx,
  bvColor,
  bvContentSx,
  bvPageSx,
  bvRadius,
  bvTransition,
  bvType,
  tabular,
} from './brandVendorsTokens';
import { bvRoutes } from './brandVendorsRoutes';
import {
  BvButton,
  BvSearchInput,
  BvEmptyState,
  BvPageHeader,
  IconPlus,
  StatusDot,
} from './components/BrandVendorsPrimitives';
import { ConfirmDialog, CreatableSelect, RegionSelect } from '../../components/ui';
import { ValidatedTextField } from '../../components/ui/ValidatedTextField';
import { regionCountryCode } from '../../utils/regionCountry';
import {
  validateEmail,
  validatePhone,
  normalizePhone,
  validateUrl,
  validatePostalCode,
  normalizePostalCode,
  validateTaxId,
  normalizeTaxId,
} from '../../utils/inputValidators';
import { useToast } from '../../contexts/ToastContext';

// The header and each row are separate grids, so every track must be a fixed or
// minmax size (never `auto`/`max-content`) or the columns drift out of line.
// The last track holds three `sm` BvButtons (Edit · Invite · Delete, ~165px with
// gaps). It used to be 76px, and the overflow spilled leftward over the Users and
// Stores cells, hiding their values (MOT-90). Keep it wide enough for the buttons.
const GRID = 'minmax(118px,2.2fr) 82px minmax(94px,1fr) minmax(92px,2.1fr) 48px 56px 180px';

const VERIFICATION: Record<
  OrganizationDirectoryRow['verification'],
  { dot: string; word: string; ink: string }
> = {
  verified: { dot: bvColor.success, word: 'Verified', ink: bvColor.success },
  pending_review: { dot: bvColor.warning, word: 'Pending', ink: bvColor.warning },
  rejected: { dot: bvColor.danger, word: 'Rejected', ink: bvColor.danger },
};

/** `'store'` shows up on some legacy records; everywhere else it's `camera_store`. */
const normalizeOrgType = (type?: string): OrganizationType => {
  if (type === 'store') return 'camera_store';
  return type === 'vendor' ? 'vendor' : 'camera_store';
};

const emptyOrgForm: OrganizationMutationPayload = {
  name: '',
  type: 'camera_store',
  status: 'active',
  contactInfo: {
    primaryContact: { name: '', email: '', phone: '' },
    website: '',
  },
  address: {
    street: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
  },
  businessInfo: {
    taxId: '',
    businessLicense: '',
    description: '',
  },
  vendorType: 'manufacturer',
  catalogBrandIds: [],
};

/** US camera stores must supply an EIN (mirrors the API's admin create check). */
const einRequired = (form: OrganizationMutationPayload): boolean =>
  form.type === 'camera_store' && regionCountryCode(form.address.country) === 'US';

const formFromDetail = (org: OrganizationDetail): OrganizationMutationPayload => ({
  name: org.name || '',
  type: normalizeOrgType(org.type),
  status: (org.status as OrganizationStatus) || 'active',
  contactInfo: {
    primaryContact: {
      name: org.contactInfo?.primaryContact?.name || '',
      email: org.contactInfo?.primaryContact?.email || '',
      phone: org.contactInfo?.primaryContact?.phone || '',
    },
    website: org.contactInfo?.website || '',
  },
  address: {
    street: org.address?.street || '',
    city: org.address?.city || '',
    state: org.address?.state || '',
    country: org.address?.country || '',
    zipCode: org.address?.zipCode || org.address?.zip || '',
  },
  businessInfo: {
    // Self-serve signup stores the tax ID flat as `taxId`; this dialog writes
    // `businessInfo.taxId`. Show whichever is on file.
    taxId: org.businessInfo?.taxId || (org as OrganizationDetail & { taxId?: string }).taxId || '',
    businessLicense: org.businessInfo?.businessLicense || '',
    description: org.businessInfo?.description || org.vendorInfo?.description || '',
  },
  vendorType: org.vendorType || 'manufacturer',
  catalogBrandIds: org.catalogBrandIds || [],
});

export const BrandVendorsOrganizationsPage: React.FC = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');

  // Brand pages, the peek drawer and the dashboard's Notify control all deep-link
  // here with ?org=<id>. Highlight and scroll to that row rather than dropping
  // the reader at the top of an unsorted table.
  const highlightedOrgId = params.get('org');
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['organization-directory'],
    queryFn: () => brandDirectoryApi.listOrganizations(),
  });

  const organizations = useMemo(() => data?.organizations || [], [data]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return organizations;
    return organizations.filter((org) =>
      [org.name, org.adminEmail || '', ...org.brandsClaimed.map((brand) => brand.brandName)]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [organizations, query]);

  // The CTA opens the approvals queue, so it must count claim requests waiting
  // there — not organizations whose own verification is pending. They diverge.
  const pendingClaims = data?.pendingClaims || 0;

  // Scroll once the highlighted row has actually rendered.
  useEffect(() => {
    if (highlightedOrgId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightedOrgId, rows.length]);

  // A search that hides the highlighted row makes the highlight misleading.
  const clearHighlight = () => {
    if (highlightedOrgId) setParams({}, { replace: true });
  };

  // ---- Organization create/edit dialog ------------------------------------
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [orgForm, setOrgForm] = useState<OrganizationMutationPayload>(emptyOrgForm);

  const editingOrgQuery = useQuery({
    queryKey: ['organization', editingOrgId],
    queryFn: () => brandDirectoryApi.getOrganization(editingOrgId as string),
    enabled: orgDialogOpen && !!editingOrgId,
  });

  useEffect(() => {
    if (editingOrgQuery.data) {
      setOrgForm(formFromDetail(editingOrgQuery.data));
    }
  }, [editingOrgQuery.data]);

  const openCreateOrg = () => {
    setEditingOrgId(null);
    setOrgForm(emptyOrgForm);
    setOrgDialogOpen(true);
  };

  const openEditOrg = (org: OrganizationDirectoryRow) => {
    setEditingOrgId(org.id);
    setOrgForm(emptyOrgForm);
    setOrgDialogOpen(true);
  };

  const closeOrgDialog = () => {
    setOrgDialogOpen(false);
    setEditingOrgId(null);
    setOrgForm(emptyOrgForm);
    createOrgMutation.reset();
    updateOrgMutation.reset();
  };

  const createOrgMutation = useMutation({
    mutationFn: (body: OrganizationMutationPayload) => brandDirectoryApi.createOrganization(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-directory'] });
      showToast('Organization created', 'success');
      closeOrgDialog();
    },
  });

  const updateOrgMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: OrganizationMutationPayload }) =>
      brandDirectoryApi.updateOrganization(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-directory'] });
      showToast('Organization updated', 'success');
      closeOrgDialog();
    },
  });

  const submitOrgForm = () => {
    const payload: OrganizationMutationPayload = {
      ...orgForm,
      businessInfo: {
        ...orgForm.businessInfo,
        taxId: normalizeTaxId(orgForm.businessInfo.taxId ?? '', regionCountryCode(orgForm.address.country)),
      },
      vendorType: orgForm.type === 'vendor' ? orgForm.vendorType : undefined,
      catalogBrandIds: orgForm.type === 'vendor' ? orgForm.catalogBrandIds : undefined,
    };
    if (editingOrgId) {
      updateOrgMutation.mutate({ id: editingOrgId, body: payload });
    } else {
      createOrgMutation.mutate(payload);
    }
  };

  const orgMutationError = (createOrgMutation.error || updateOrgMutation.error) as Error | null;
  const orgMutationPending = createOrgMutation.isPending || updateOrgMutation.isPending;
  const orgDialogLoading = !!editingOrgId && editingOrgQuery.isLoading;

  // ---- Invite dialog --------------------------------------------------------
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteOrg, setInviteOrg] = useState<OrganizationDirectoryRow | null>(null);
  const [inviteForm, setInviteForm] = useState<{ email: string; role: OrganizationRole; message: string }>({
    email: '',
    role: 'employee',
    message: '',
  });

  const openInvite = (org: OrganizationDirectoryRow) => {
    setInviteOrg(org);
    setInviteForm({ email: '', role: 'employee', message: '' });
    setInviteDialogOpen(true);
  };

  const closeInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteOrg(null);
    inviteMutation.reset();
  };

  const inviteMutation = useMutation({
    mutationFn: () =>
      brandDirectoryApi.inviteOrganizationUser(inviteOrg!.id, {
        email: inviteForm.email.trim(),
        role: inviteForm.role,
        notes: inviteForm.message.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-directory'] });
      showToast('Invitation sent', 'success');
      closeInviteDialog();
    },
  });

  // ---- Row overflow menu + delete confirm -------------------------------
  const [menuState, setMenuState] = useState<{ anchor: HTMLElement; org: OrganizationDirectoryRow } | null>(null);
  const [deleteOrg, setDeleteOrg] = useState<OrganizationDirectoryRow | null>(null);

  const deleteOrgMutation = useMutation({
    mutationFn: (id: string) => brandDirectoryApi.deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-directory'] });
      showToast('Organization deleted', 'success');
      setDeleteOrg(null);
    },
  });

  const closeDeleteDialog = () => {
    setDeleteOrg(null);
    deleteOrgMutation.reset();
  };

  return (
    <Box sx={bvPageSx}>
      <Box sx={{ ...bvContentSx, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <BvPageHeader
          title="Organizations"
          description="Vendor and retail organizations on the platform, and which brands each one has claimed. An org is verified against a brand only once an admin approves its claim."
          actions={
            <>
              <BvButton size="lg" tone="primary" startIcon={<IconPlus size={14} />} onClick={openCreateOrg}>
                Add organization
              </BvButton>
              <BvButton size="lg" tone="warning" onClick={() => navigate(bvRoutes.approvals())}>
                Approvals queue
                {pendingClaims > 0 && (
                  <Box component="span" sx={{ ml: '6px', fontWeight: 600, ...tabular }}>
                    {pendingClaims} pending
                  </Box>
                )}
              </BvButton>
            </>
          }
        />

        {error && <Alert severity="error">{(error as Error).message}</Alert>}

        <Box sx={bvCardSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              px: '16px',
              py: '11px',
              bgcolor: bvColor.page,
              borderBottom: `1px solid ${bvColor.border}`,
            }}
          >
            <BvSearchInput
              ariaLabel="Search organizations"
              value={query}
              onChange={(next) => {
                setQuery(next);
                clearHighlight();
              }}
              placeholder="Search org, contact or brand"
            />
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.caption, color: bvColor.muted, ...tabular }}>
              {rows.length} of {organizations.length} organizations
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', lg: 'grid' },
              gridTemplateColumns: { xs: '1fr', lg: GRID },
              columnGap: '10px',
              px: '16px',
              py: '9px',
              bgcolor: bvColor.page,
              borderBottom: `1px solid ${bvColor.border}`,
              ...bvType.caption,
              color: bvColor.muted,
            }}
          >
            <Box>Organization</Box>
            <Box>Type</Box>
            <Box>Verification</Box>
            <Box>Brands claimed</Box>
            <Box sx={{ textAlign: 'right' }}>Users</Box>
            <Box sx={{ textAlign: 'right' }}>Stores</Box>
            <Box />
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: '56px' }}>
              <CircularProgress size={22} />
            </Box>
          ) : rows.length === 0 ? (
            <BvEmptyState headline="No organizations in this view." py="56px" />
          ) : (
            rows.map((org) => {
              const verification = VERIFICATION[org.verification];
              const highlighted = org.id === highlightedOrgId;
              return (
                <Box
                  key={org.id}
                  ref={highlighted ? highlightRef : undefined}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: GRID },
                    columnGap: '10px',
                    rowGap: '8px',
                    alignItems: 'center',
                    px: '16px',
                    py: '11px',
                    borderBottom: `1px solid ${bvColor.borderSubtle}`,
                    transition: bvTransition,
                    bgcolor: highlighted ? bvColor.surfaceTint : 'transparent',
                    boxShadow: highlighted ? `inset 3px 0 0 ${bvColor.accent}` : 'none',
                    '&:hover': { bgcolor: highlighted ? bvColor.surfaceTint : bvColor.page },
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  {/* organization */}
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        ...bvType.tablePrimary,
                        color: bvColor.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {org.name}
                    </Box>
                    <Box
                      sx={{
                        ...bvType.caption,
                        color: bvColor.muted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {org.adminEmail ? org.adminEmail.trim().toLowerCase() : 'No contact on file'}
                    </Box>
                  </Box>

                  {/* type */}
                  <Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        px: '8px',
                        py: '2px',
                        borderRadius: `${bvRadius.pill}px`,
                        border: `1px solid ${bvColor.border}`,
                        bgcolor: bvColor.surface,
                        color: bvColor.ink3,
                        ...bvType.stagePill,
                      }}
                    >
                      {org.orgType === 'vendor' ? 'Vendor' : 'Retail'}
                    </Box>
                  </Box>

                  {/* verification */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <StatusDot color={verification.dot} size={7} />
                    <Box sx={{ ...bvType.tableSecondary, color: verification.ink }}>{verification.word}</Box>
                  </Box>

                  {/* brands claimed */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', minWidth: 0 }}>
                    {org.brandsClaimed.length === 0 ? (
                      <Box sx={{ ...bvType.caption, color: bvColor.muted }}>none</Box>
                    ) : (
                      org.brandsClaimed.map((brand) => {
                        const confirmed = brand.status === 'confirmed';
                        const chipSx = {
                          px: '7px',
                          py: '2px',
                          borderRadius: `${bvRadius.chip}px`,
                          border: `1px solid ${confirmed ? bvColor.successBorder : bvColor.warningBorder}`,
                          color: confirmed ? bvColor.success : bvColor.warning,
                          bgcolor: bvColor.surface,
                          ...bvType.microCaption,
                          whiteSpace: 'nowrap',
                        };
                        // Confirmed claims link through to the brand; a requested
                        // claim has no brand page relationship yet, so it stays inert.
                        return confirmed ? (
                          <ButtonBase
                            key={brand.brandId}
                            onClick={() => navigate(bvRoutes.brand(brand.brandId))}
                            sx={{
                              ...chipSx,
                              transition: bvTransition,
                              '&:hover': { borderColor: bvColor.accent, color: bvColor.accentHover },
                              '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                            }}
                          >
                            {brand.brandName}
                          </ButtonBase>
                        ) : (
                          <Box key={brand.brandId} component="span" sx={chipSx}>
                            {brand.brandName} · requested
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  {/* users */}
                  <Box sx={{ textAlign: 'right', ...bvType.tableSecondary, color: bvColor.ink3, ...tabular }}>
                    {org.userCount}
                  </Box>

                  {/* stores */}
                  <Box sx={{ textAlign: 'right', ...bvType.tableSecondary, color: bvColor.ink3, ...tabular }}>
                    {org.storeCount === null ? '—' : org.storeCount}
                  </Box>

                  {/* row actions — wrap rather than overflow, so they can never cover Users/Stores */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '5px',
                      justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                    }}
                  >
                    <BvButton size="sm" aria-label={`Edit ${org.name}`} onClick={() => openEditOrg(org)}>
                      Edit
                    </BvButton>
                    <BvButton size="sm" aria-label={`Invite user to ${org.name}`} onClick={() => openInvite(org)}>
                      Invite
                    </BvButton>
                    {/* Delete lives in the overflow menu so it is never one mis-click from Edit/Invite. */}
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        aria-label={`More actions for ${org.name}`}
                        aria-haspopup="menu"
                        onClick={(event) => setMenuState({ anchor: event.currentTarget, org })}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={menuState?.anchor}
        open={!!menuState}
        onClose={() => setMenuState(null)}
      >
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            const target = menuState?.org || null;
            setMenuState(null);
            setDeleteOrg(target);
          }}
        >
          Delete organization…
        </MenuItem>
      </Menu>

      {/* Create / edit organization */}
      <Dialog
        open={orgDialogOpen}
        onClose={closeOrgDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ ...bvType.cardTitle, fontSize: 16 }}>
          {editingOrgId ? 'Edit organization' : 'Add organization'}
        </DialogTitle>
        <DialogContent>
          {orgDialogLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: '40px' }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Box>
                <Box sx={{ ...bvType.eyebrow, mb: '8px' }}>Basic</Box>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Organization name"
                    value={orgForm.name}
                    onChange={(e) => setOrgForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                  <Stack direction="row" spacing={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={orgForm.type}
                        onChange={(e) =>
                          setOrgForm((prev) => ({ ...prev, type: e.target.value as OrganizationType }))
                        }
                      >
                        <MenuItem value="camera_store">Camera store</MenuItem>
                        <MenuItem value="vendor">Vendor</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={orgForm.status}
                        onChange={(e) =>
                          setOrgForm((prev) => ({ ...prev, status: e.target.value as OrganizationStatus }))
                        }
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="pending_verification">Pending verification</MenuItem>
                        {/* Legacy status: shown only so an org already carrying it still reads, never offered as a choice. */}
                        {orgForm.status === 'pending_approval' && (
                          <MenuItem value="pending_approval" disabled>
                            Pending approval (older status)
                          </MenuItem>
                        )}
                        <MenuItem value="suspended">Suspended</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              </Box>

              <Box>
                <Box sx={{ ...bvType.eyebrow, mb: '8px' }}>Contact</Box>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Primary contact name"
                    value={orgForm.contactInfo.primaryContact.name}
                    onChange={(e) =>
                      setOrgForm((prev) => ({
                        ...prev,
                        contactInfo: {
                          ...prev.contactInfo,
                          primaryContact: { ...prev.contactInfo.primaryContact, name: e.target.value },
                        },
                      }))
                    }
                  />
                  <Stack direction="row" spacing={1.5}>
                    <ValidatedTextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      validate={validateEmail}
                      value={orgForm.contactInfo.primaryContact.email}
                      onChange={(e) =>
                        setOrgForm((prev) => ({
                          ...prev,
                          contactInfo: {
                            ...prev.contactInfo,
                            primaryContact: { ...prev.contactInfo.primaryContact, email: e.target.value },
                          },
                        }))
                      }
                    />
                    <ValidatedTextField
                      fullWidth
                      size="small"
                      label="Phone"
                      validate={validatePhone}
                      normalize={normalizePhone}
                      onNormalized={(v) =>
                        setOrgForm((prev) => ({
                          ...prev,
                          contactInfo: {
                            ...prev.contactInfo,
                            primaryContact: { ...prev.contactInfo.primaryContact, phone: v },
                          },
                        }))
                      }
                      value={orgForm.contactInfo.primaryContact.phone}
                      onChange={(e) =>
                        setOrgForm((prev) => ({
                          ...prev,
                          contactInfo: {
                            ...prev.contactInfo,
                            primaryContact: { ...prev.contactInfo.primaryContact, phone: e.target.value },
                          },
                        }))
                      }
                    />
                  </Stack>
                  <ValidatedTextField
                    fullWidth
                    size="small"
                    label="Website"
                    validate={(v) => validateUrl(v, { allowSchemeless: true })}
                    value={orgForm.contactInfo.website}
                    onChange={(e) =>
                      setOrgForm((prev) => ({
                        ...prev,
                        contactInfo: { ...prev.contactInfo, website: e.target.value },
                      }))
                    }
                  />
                </Stack>
              </Box>

              <Box>
                <Box sx={{ ...bvType.eyebrow, mb: '8px' }}>Address</Box>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Street address"
                    value={orgForm.address.street}
                    onChange={(e) =>
                      setOrgForm((prev) => ({ ...prev, address: { ...prev.address, street: e.target.value } }))
                    }
                  />
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={orgForm.address.city}
                      onChange={(e) =>
                        setOrgForm((prev) => ({ ...prev, address: { ...prev.address, city: e.target.value } }))
                      }
                    />
                    <RegionSelect
                      fullWidth
                      country={regionCountryCode(orgForm.address.country)}
                      label="State/Province"
                      value={orgForm.address.state}
                      onChange={(v) => setOrgForm((prev) => ({ ...prev, address: { ...prev.address, state: v } }))}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <CreatableSelect
                      sx={{ width: '100%' }}
                      label="Country"
                      value={orgForm.address.country}
                      options={[
                        { value: 'US', hint: 'United States' },
                        { value: 'CA', hint: 'Canada' },
                      ]}
                      onChange={(v) =>
                        setOrgForm((prev) => {
                          // A state code from one country's list means nothing in another's.
                          const switched = regionCountryCode(v) !== regionCountryCode(prev.address.country);
                          return { ...prev, address: { ...prev.address, country: v, state: switched ? '' : prev.address.state } };
                        })
                      }
                    />
                    <ValidatedTextField
                      fullWidth
                      size="small"
                      label="Postal code"
                      validate={(v) => validatePostalCode(v, regionCountryCode(orgForm.address.country))}
                      normalize={(v) => normalizePostalCode(v, regionCountryCode(orgForm.address.country))}
                      onNormalized={(v) =>
                        setOrgForm((prev) => ({ ...prev, address: { ...prev.address, zipCode: v } }))
                      }
                      value={orgForm.address.zipCode}
                      onChange={(e) =>
                        setOrgForm((prev) => ({ ...prev, address: { ...prev.address, zipCode: e.target.value } }))
                      }
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box>
                <Box sx={{ ...bvType.eyebrow, mb: '8px' }}>Business</Box>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5}>
                    <ValidatedTextField
                      fullWidth
                      size="small"
                      required={einRequired(orgForm)}
                      label={einRequired(orgForm) ? 'EIN' : 'Tax ID'}
                      validate={(v) => validateTaxId(v, regionCountryCode(orgForm.address.country))}
                      normalize={(v) => normalizeTaxId(v, regionCountryCode(orgForm.address.country))}
                      onNormalized={(v) =>
                        setOrgForm((prev) => ({ ...prev, businessInfo: { ...prev.businessInfo, taxId: v } }))
                      }
                      value={orgForm.businessInfo.taxId}
                      onChange={(e) =>
                        setOrgForm((prev) => ({
                          ...prev,
                          businessInfo: { ...prev.businessInfo, taxId: e.target.value },
                        }))
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Business license"
                      value={orgForm.businessInfo.businessLicense}
                      onChange={(e) =>
                        setOrgForm((prev) => ({
                          ...prev,
                          businessInfo: { ...prev.businessInfo, businessLicense: e.target.value },
                        }))
                      }
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="Description"
                    value={orgForm.businessInfo.description}
                    onChange={(e) =>
                      setOrgForm((prev) => ({
                        ...prev,
                        businessInfo: { ...prev.businessInfo, description: e.target.value },
                      }))
                    }
                  />
                </Stack>
              </Box>

              {orgForm.type === 'vendor' && (
                <Box>
                  <Box sx={{ ...bvType.eyebrow, mb: '8px' }}>Vendor catalog access</Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vendor type</InputLabel>
                    <Select
                      label="Vendor type"
                      value={orgForm.vendorType}
                      onChange={(e) =>
                        setOrgForm((prev) => ({
                          ...prev,
                          vendorType: e.target.value as OrganizationVendorType,
                        }))
                      }
                    >
                      <MenuItem value="manufacturer">Manufacturer</MenuItem>
                      <MenuItem value="distributor">Distributor</MenuItem>
                      <MenuItem value="wholesaler">Wholesaler</MenuItem>
                      <MenuItem value="accessory_brand">Accessory brand</MenuItem>
                      <MenuItem value="software_provider">Software provider</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {orgMutationError && <Alert severity="error">{orgMutationError.message}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <BvButton onClick={closeOrgDialog}>Cancel</BvButton>
          <BvButton
            tone="primary"
            disabled={
              !orgForm.name.trim() ||
              orgMutationPending ||
              orgDialogLoading ||
              validateTaxId(orgForm.businessInfo.taxId ?? '', regionCountryCode(orgForm.address.country)) !== null ||
              (!editingOrgId && einRequired(orgForm) && !orgForm.businessInfo.taxId?.trim())
            }
            onClick={submitOrgForm}
          >
            {editingOrgId ? 'Save changes' : 'Create organization'}
          </BvButton>
        </DialogActions>
      </Dialog>

      {/* Invite user */}
      <Dialog
        open={inviteDialogOpen}
        onClose={closeInviteDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ ...bvType.cardTitle, fontSize: 16 }}>
          Invite user{inviteOrg ? ` to ${inviteOrg.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ValidatedTextField
              fullWidth
              required
              size="small"
              label="Email address"
              type="email"
              validate={validateEmail}
              placeholder="user@example.com"
              value={inviteForm.email}
              onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={inviteForm.role}
                onChange={(e) =>
                  setInviteForm((prev) => ({ ...prev, role: e.target.value as OrganizationRole }))
                }
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="owner">Owner</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Message (optional)"
              placeholder="Add a personal note to the invitation..."
              value={inviteForm.message}
              onChange={(e) => setInviteForm((prev) => ({ ...prev, message: e.target.value }))}
            />
            {inviteMutation.error && (
              <Alert severity="error">{(inviteMutation.error as Error).message}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <BvButton onClick={closeInviteDialog}>Cancel</BvButton>
          <BvButton
            tone="primary"
            disabled={!inviteForm.email.trim() || inviteMutation.isPending}
            onClick={() => inviteMutation.mutate()}
          >
            Send invitation
          </BvButton>
        </DialogActions>
      </Dialog>

      {/* Delete organization */}
      <ConfirmDialog
        open={!!deleteOrg}
        title="Delete organization"
        message={
          <>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              This will permanently delete {deleteOrg?.name} and remove its memberships and approvals. This
              cannot be undone.
            </Alert>
            {deleteOrgMutation.error && (
              <Alert severity="error">{(deleteOrgMutation.error as Error).message}</Alert>
            )}
          </>
        }
        confirmLabel="Delete organization"
        confirmColor="error"
        loading={deleteOrgMutation.isPending}
        onConfirm={() => deleteOrg && deleteOrgMutation.mutate(deleteOrg.id)}
        onClose={closeDeleteDialog}
      />
    </Box>
  );
};

export default BrandVendorsOrganizationsPage;
